import { CommandReceivedResult } from '@share/domain-objects/commands/command-received-result';
import { PeSignupStatus } from '../data-services/pe-signup-status';
import { PeSignupState } from '../dashboard/dashboard/pe-type.enum';
import {
  AdminOffice,
  Company,
  Domain,
  EPNIntegration,
  Integration,
  MessageConfig,
  MessagingPhoneNumber,
  PeFeatureToggleDto,
  SelectedMessageConfigContent,
} from '@openapi';
import { create } from '@share/utils/create';
import { TimeBeforeTypeEnum } from '@share/domain-objects/company/integration/epn-integration/time-before-type.enum';
import { environment } from '../../environments/environment';
import {
  APPLICATION_NAMES,
  EPM_CLIENT_ID,
  EYEFINITY_CLIENT_ID,
  PeConnAccessToken,
} from '@share/constants';
import { Observable, of } from 'rxjs';
import { DropdownValueObject } from '@share/ui-components/dropdown/dropdown-value-object';
import {
  getMessagingConfigEnumAsNumber,
  MessagingConfigTypeEnum,
} from '@share/domain-objects/company/integration/epn-integration/messaging-config-type.enum';
import { ContentGroupTypeEnum } from '@share/domain-objects/messaging-configuration/content-group-type.enum';
import { MessageConfigContent } from '@share/domain-objects/messaging-configuration/message-config-content';
import RecallWeekOfEnum = EPNIntegration.RecallWeekOfEnum;
import RecallDayOfEnum = EPNIntegration.RecallDayOfEnum;

export function mockCommandReceivedResult(): CommandReceivedResult {
  return {
    success: true,
    errorMessage: 'Crap hit the fan',
    longRunning: false,
  } as CommandReceivedResult;
}

export function mockPeSignupStatus(): PeSignupStatus {
  const status = new PeSignupStatus();
  status.ewebextra = PeSignupState.SignUp;
  status.patientNotifications = PeSignupState.SignUp;
  return status;
}

export function mockCompanyData(): Company[] {
  return [
    {
      ...create<Company>(),
      companyName: "Bob's Company",
      practiceId: '12354',
      efan: '9000000000',
      alan: '9000000000',
      domains: [
        {
          ...create<Domain>(),
          efan: '9000000000',
          domainName: 'WeAreCool.com',
        },
      ],
      integration: {
        ...create<Integration>(),
        formsOn: false,
        schedulerOn: true,
        notificationEmailAddresses: ['bob@bob.COM'],
        epmIntegrated: true,
        adminOffices: [
          {
            ...create<AdminOffice>(),
            displayName: "bob's office 1",
            id: 1,
            timeZone: 2,
            phoneNumber: '5556667687',
          },
          {
            ...create<AdminOffice>(),
            displayName: 'hell hath no fury',
            id: 2,
            timeZone: 3,
          },
        ], // epm locations
        epnIntegration: {
          ...mockEpnIntegrationData(),
        },
        featureToggles: [...mockFeatureToggles([1, 2], 12345, true)],
      },
    } as Company,
  ];
}
export function mockPhoneNumber(id?: number): MessagingPhoneNumber {
  return {
    ...create<MessagingPhoneNumber>(),
    id: id ? id : 1,
    messagingPhoneNumber: '123456799',
    officePhoneNumber: '(916) 851-6969',
    active: true,
    messageConfigs: [
      {
        ...create<MessageConfig>(),
        epmLocationId: 1,
        type: MessagingConfigTypeEnum.REMINDERS,
        content:
          'Hi ${reminder.patientFirstName}, you have an appointment tomorrow. Come stop by and see us at ${reminder.practice.name}. ${crazyTimes}.',
        active: true,
        timeBefore: 3,
        hourToRun: '11',
      } as MessageConfig,
      {
        ...create<MessageConfig>(),
        epmLocationId: 1,
        type: MessagingConfigTypeEnum.DAY_OF_REMINDERS,
        active: true,
        timeBefore: 5,
        hourToRun: '12',
      } as MessageConfig,
      {
        ...create<MessageConfig>(),
        epmLocationId: 2,
        type: MessagingConfigTypeEnum.REMINDERS,
        active: true,
        timeBefore: 13,
        hourToRun: '13',
      } as MessageConfig,
    ],
  };
}

export function mockSelectedMessageConfigContent(
  contentIds: number[],
  epmLocationId = 123,
  messageConfigType = 0,
  practiceId = 123,
): SelectedMessageConfigContent[] {
  const content: SelectedMessageConfigContent[] = [];
  contentIds.forEach((id, index) => {
    content.push({
      id: index,
      messageConfigContentId: id,
      epmLocationId: epmLocationId,
      messageConfigTypeId: messageConfigType,
      practiceId: practiceId,
    } as SelectedMessageConfigContent);
  });
  return content;
}

export function mockEpnIntegrationData(): EPNIntegration {
  return {
    domainName: 'bob.com',
    remindersOn: false,
    recallsOn: true,
    dayOfRemindersOn: false,
    orderNotificationsOn: true,
    recallHourToRun: '12',
    recallWeekOf: RecallWeekOfEnum.Second,
    recallDayOf: RecallDayOfEnum.Monday,
    timeBeforeType: TimeBeforeTypeEnum.MONTHLY,
    selectedMessageConfigContent: [
      ...mockSelectedMessageConfigContent([1], 1, 0, 123),
      ...mockSelectedMessageConfigContent([3], 1, 3, 123),
      ...mockSelectedMessageConfigContent([7], 1, 4, 123),
      ...mockSelectedMessageConfigContent([9], null, 7, 123),
    ],
    messagingPhoneNumbers: [mockPhoneNumber(1), mockPhoneNumber(2)],
  } as EPNIntegration;
}
export function mockFeatureToggles(
  epmLocationIds: number[],
  practiceId: number,
  withCompanyWide = true,
): PeFeatureToggleDto[] {
  const toggles = [];
  for (let i = 0; i < epmLocationIds.length; i++) {
    let toggle = { ...create<PeFeatureToggleDto>() } as PeFeatureToggleDto;
    toggle.epmLocationId = epmLocationIds[i];
    toggle.activeScheduleTypeId = 0;
    toggle.toggleScheduleTypeId = 1;
    toggle.titleUserDescription = 'same day appointments #' + i;
    toggle.tooltipUserDescription = 'This is the tooltip #' + i;
    toggle.labelUserDescription = 'This is the label #' + i;
    toggle.possibleValues = '1 hour:1,2 hours:2,3 hours:3,4 hours:4';
    toggle.selectedValue = null;
    toggle.practiceId = practiceId;
    toggle.active = false;
    toggle.sequence = 1;
    toggles.push(toggle);

    toggle = { ...create<PeFeatureToggleDto>() } as PeFeatureToggleDto;
    toggle.epmLocationId = epmLocationIds[i];
    toggle.practiceId = practiceId;
    toggle.featureTypeId = 2;
    toggle.titleUserDescription = 'unmatched patient #' + i;
    toggle.tooltipUserDescription = 'This is the tooltip #' + i;
    toggle.labelUserDescription = 'This is the label #' + i;
    toggle.possibleValues = null;
    toggle.selectedValue = null;
    toggle.active = false;
    toggle.sequence = 2;
    toggles.push(toggle);
  }
  if (withCompanyWide) {
    const toggle = { ...create<PeFeatureToggleDto>() } as PeFeatureToggleDto;
    toggle.epmLocationId = null;
    toggle.activeScheduleTypeId = 6;
    toggle.toggleScheduleTypeId = 6;
    toggle.titleUserDescription = 'This is the title for company wide';
    toggle.tooltipUserDescription = 'This is the tooltip company wide';
    toggle.labelUserDescription = 'This is the label company wide';
    toggle.possibleValues = '1 hour:1,2 hours:2,3 hours:3,4 hours:4';
    toggle.selectedValue = null;
    toggle.practiceId = practiceId;
    toggle.active = false;
    toggle.sequence = 1;
    toggles.push(toggle);
  }
  return toggles;
}

export function mockPeConnAccessToken(): PeConnAccessToken {
  return {
    ...new PeConnAccessToken(),
    token_type: 'Bearer',
    access_token: mockAccessToken(),
    signupStatusEpn: PeSignupState.SignUp,
    signupStatusEwx: PeSignupState.SignUp,
    principal: 'trapper_eweb',
    companyId: '936',
    application:
      environment &&
      environment.mockDataBaseApplication === APPLICATION_NAMES.epm
        ? APPLICATION_NAMES.epm
        : APPLICATION_NAMES.eyefinity,
    efan: '9000000000',
    clientId:
      environment &&
      environment.mockDataBaseApplication === APPLICATION_NAMES.epm
        ? EPM_CLIENT_ID
        : EYEFINITY_CLIENT_ID,
    expires_in: 10000,
  };
}

export function mockMessageContentDropdownData$(): Observable<
  DropdownValueObject[]
> {
  const messageConfigContent = [];
  const data: DropdownValueObject[] = [];
  const shortMessage = 'Please this is s shorty';
  const longMessage =
    "This is super long, Because ${reminders.awesome} are the thing and we'll get rid of this code someday.";
  let getDataFromApi = mockMessageConfigContent(
    MessagingConfigTypeEnum.REMINDERS,
    ContentGroupTypeEnum.SIMPLE,
  );
  getDataFromApi.id = 1;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 2;
  getDataFromApi.content = getDataFromApi.content + ' ' + longMessage;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 3;
  getDataFromApi.content = shortMessage;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content = 'This is the real message that should load up';
  getDataFromApi.id = 11;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // greeting

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 4;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.GREETING;
  getDataFromApi.content = 'Hello ${reminder.patientFirstName}';
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.GREETING;
  getDataFromApi.content = 'Hi ${reminder.patientFirstName}';
  getDataFromApi.id = 5;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // purpose

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 6;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.PURPOSE;
  getDataFromApi.content =
    'you have an eye appointment with ${reminder.providerLastName}$';
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content =
    'you are seeing ${reminder.providerLastName}$ at $reminder.practice.name}';
  getDataFromApi.id = 7;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.PURPOSE;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // when
  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 8;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.WHEN;
  getDataFromApi.content = 'at ${startTime} on ${appointmentDate}.';
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content = 'on ${appointmentDate} at ${startTime}.';
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.WHEN;
  getDataFromApi.id = 9;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // optional
  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 10;
  getDataFromApi.content = 'Please wear a mask.';
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.OPTIONAL;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content = 'We are located at $ADDRESS$.';
  getDataFromApi.id = 12;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.OPTIONAL;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  return of(data);
}

export function mockMessageConfigContent(
  configTypeId: MessagingConfigTypeEnum,
  contentTypeId: ContentGroupTypeEnum,
): MessageConfigContent {
  const content = new MessageConfigContent();
  content.content =
    'Hi there buddy, howzit going? You have an eyeball appointment today with Dr Bob at 3 pm on 10/22/20.  We really hope you show up and please wear a mask.';
  content.messageConfigTypeId = getMessagingConfigEnumAsNumber(configTypeId);
  content.contentGroupTypeId = contentTypeId;
  content.id = 1;
  return content;
}

export function mockAccessToken(): string {
  return 'thisisamockaccesstoken1234567890';
}
